import { Button, Card, Grid, GridColumn, GridRow } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useConfirm } from '../../hooks/useConfirm';
import { useUserConfigContext } from '../../hooks/useUserConfigContext';
import {
  SETTINGS_REGISTRY,
  SETTINGS_SCOPES,
  SettingsDisplayField,
  SettingsScope,
} from '../../stores/userConfig/settings/schemas';
import styles from './UserSettingsCard.module.scss';

/**
 * Uebersicht der gespeicherten Ansichtseinstellungen mit Zuruecksetzen je Bereich.
 *
 * Eine Zeile pro Bereich statt einer Liste aller Einzelwerte — welche Werte
 * verstaendlich genug sind, legt die Registry ueber `displayFields` fest. Groessen
 * und Positionen der Bereiche stehen bewusst nicht in der Liste, werden beim
 * Zuruecksetzen aber mitgeloescht.
 */
export const UserSettingsCard = observer(() => {
  const { t } = useTranslation();
  const userConfig = useUserConfigContext();

  const { ConfirmationDialog, confirm } = useConfirm({
    defaultTitle: t('userSettings.resetAll'),
    defaultMessage: t('userSettings.resetAllConfirm'),
    defaultConfirmText: t('userSettings.reset'),
  });

  const tableCount = userConfig.tableConfigs.size;

  const resetAll = async () => {
    if (!(await confirm())) return;
    await Promise.all([userConfig.resetSettings(), userConfig.resetTableConfigs()]);
  };

  return (
    <Card header={{ headline: t('userSettings.title') }} wrapperClass='mb-6'>
      <Grid useFormGap>
        {SETTINGS_SCOPES.map((scope) => (
          <ScopeRow key={scope} scope={scope} />
        ))}

        <SettingsRow
          label={t('userSettings.scope.tables')}
          values={tableCount > 0 ? t('userSettings.tables.count', { count: tableCount }) : t('userSettings.default')}
          disabled={tableCount === 0}
          onReset={() => void userConfig.resetTableConfigs()}
        />

        <GridRow spacingBottom='s'>
          <GridColumn width={12}>
            <Button hierarchy='tertiary' onClick={() => void resetAll()}>
              {t('userSettings.resetAll')}
            </Button>
          </GridColumn>
        </GridRow>
      </Grid>

      <ConfirmationDialog />
    </Card>
  );
});

/**
 * Eine Zeile der Uebersicht: Bereich, gespeicherte Werte, Zuruecksetzen.
 *
 * Die drei Spalten sind `GridColumn`s — der Button steht damit in einer eigenen
 * Spalte und wird nicht von langen Werten gequetscht.
 */
const SettingsRow = ({
  label,
  values,
  disabled,
  onReset,
}: {
  label: string;
  values: React.ReactNode;
  disabled: boolean;
  onReset: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <GridRow spacingTop='none' spacingBottom='none' className={styles.row}>
      <GridColumn width={3}>
        <div className={styles.label}>{label}</div>
      </GridColumn>
      <GridColumn width={7}>
        <div className={styles.values}>{values}</div>
      </GridColumn>
      <GridColumn width={2} justify='end'>
        <Button hierarchy='secondary' disabled={disabled} onClick={onReset}>
          {t('userSettings.reset')}
        </Button>
      </GridColumn>
    </GridRow>
  );
};

const ScopeRow = observer(({ scope }: { scope: SettingsScope }) => {
  const { t } = useTranslation();
  const userConfig = useUserConfigContext();

  const entry = SETTINGS_REGISTRY[scope];
  const settings = userConfig.getSettings(scope);
  const hasStored = userConfig.hasStoredSettings(scope);

  // `displayFields` ist pro Scope typisiert; hier wird nur gelesen und formatiert.
  const fields = entry.displayFields as unknown as SettingsDisplayField<Record<string, unknown>>[];

  return (
    <SettingsRow
      label={t(entry.labelKey)}
      values={
        hasStored
          ? fields.map((field) => (
              <span key={field.key}>
                <span className={styles['value-label']}>{t(field.labelKey)}:</span>{' '}
                {t(field.format((settings as Record<string, unknown>)[field.key]))}
              </span>
            ))
          : t('userSettings.default')
      }
      disabled={!hasStored}
      onReset={() => void userConfig.resetSettings([scope])}
    />
  );
});
